/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { reopenCookieBanner } from "./utils/consentManager";
import {
  ArrowLeft, LogOut, Download, FileText, GraduationCap, Lock, Mail,
  UserCircle, RefreshCw, CheckCircle,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { RgpdNotice } from "./components/RgpdNotice";
import { toast } from "sonner";

import { EmargementApprenant } from "./components/EmargementApprenant";
import { DocumentsApprenant } from "./components/DocumentsApprenant";
import { MesFormations } from "./components/MesFormations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "mgbApprenantToken";
const USER_KEY = "mgbApprenantUser";

const getToken = () => localStorage.getItem(TOKEN_KEY);
const getUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || "null"); }
  catch { return null; }
};
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const formatBytes = (b) => {
  if (!b) return "0 o";
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

// ==================================================================
// LOGIN PAGE — /apprenant/login
// ==================================================================
export const ApprenantLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getToken()) navigate("/apprenant");
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/apprenant/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.apprenant));
      toast.success("Bienvenue !");
      navigate("/apprenant");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(135deg, #003087 0%, #2196F3 100%)" }}
      data-testid="apprenant-login-page"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#F07B1D]/10 flex items-center justify-center mb-3">
            <GraduationCap className="h-8 w-8 text-[#F07B1D]" />
          </div>
          <CardTitle className="text-2xl font-heading text-[#003087]">Espace apprenant</CardTitle>
          <CardDescription>Accédez à vos ressources et à votre suivi de formation.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="ap-email" className="text-sm font-bold text-[#003087]">
                <Mail className="inline h-3.5 w-3.5 mr-1" /> E-mail
              </Label>
              <Input
                id="ap-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre.email@exemple.fr"
                className="mt-1"
                data-testid="apprenant-login-email"
              />
            </div>
            <div>
              <Label htmlFor="ap-pass" className="text-sm font-bold text-[#003087]">
                <Lock className="inline h-3.5 w-3.5 mr-1" /> Mot de passe
              </Label>
              <Input
                id="ap-pass"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe communiqué par MGB School"
                className="mt-1"
                data-testid="apprenant-login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold py-6"
              data-testid="apprenant-login-btn"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>
          <RgpdNotice testid="rgpd-notice-apprenant-login" />
          <div className="mt-5 pt-4 border-t border-gray-100 text-center text-xs text-gray-500">
            <p>Pas encore d'identifiants ?</p>
            <p className="mt-1">
              Contactez-nous à{" "}
              <a href="mailto:contact@mgbschool.fr" className="text-[#F07B1D] underline font-semibold">
                contact@mgbschool.fr
              </a>
            </p>
          </div>
          <div className="mt-4 text-center">
            <Link to="/" className="text-sm text-gray-500 hover:text-[#003087] inline-flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> Retour au site
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================================================================
// DASHBOARD — /apprenant
// ==================================================================
export const ApprenantDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(getUser());
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    navigate("/apprenant/login");
  }, [navigate]);

  const load = useCallback(async () => {
    if (!getToken()) {
      navigate("/apprenant/login");
      return;
    }
    setLoading(true);
    try {
      const [meRes, resRes] = await Promise.all([
        axios.get(`${API}/apprenant/me`, { headers: authHeaders() }),
        axios.get(`${API}/apprenant/resources`, { headers: authHeaders() }),
      ]);
      setUser(meRes.data);
      localStorage.setItem(USER_KEY, JSON.stringify(meRes.data));
      setResources(resRes.data || []);

      // Log page view
      axios.post(`${API}/apprenant/log`, {
        event_type: "page_view",
        metadata: { page: "dashboard" },
      }, { headers: authHeaders() }).catch(() => {});
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error(err.response?.data?.detail || "Session expirée");
        logout();
      } else {
        toast.error("Erreur de chargement");
      }
    } finally {
      setLoading(false);
    }
  }, [logout, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (r) => {
    setDownloadingId(r.id);
    try {
      const res = await axios.get(`${API}/apprenant/resources/${r.id}/download`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.original_filename || r.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Téléchargement démarré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Téléchargement impossible");
    } finally {
      setDownloadingId(null);
    }
  };

  const categoryLabel = (c) => ({
    modele: "Modèle",
    tableur: "Tableur",
    checklist: "Checklist",
    livret: "Livret",
    autre: "Autre",
  }[c] || c);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="apprenant-dashboard">
      {/* Header */}
      <header className="bg-[#003087] text-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#F07B1D] flex items-center justify-center">
              <UserCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-xs text-white/70 uppercase tracking-wider">Espace apprenant</p>
              <p className="font-heading font-bold">{user.prenom} {user.nom}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={logout}
            className="text-white hover:bg-white/10"
            data-testid="apprenant-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-1" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Welcome card */}
        <Card className="border-l-4 border-l-[#F07B1D]">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500 uppercase font-semibold tracking-wider">Bienvenue</p>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#003087] mt-1">
              Bonjour {user.prenom} 👋
            </h1>
            <p className="text-gray-600 mt-2">
              Retrouvez ici vos formations (modules, quiz, certificat), vos documents de formation (convocation, attestation, certificat), votre émargement et vos ressources téléchargeables.
              Chaque téléchargement est enregistré dans votre journal de formation à des fins de traçabilité pédagogique.
            </p>
            {user.formation_code && (
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
                <Badge className="bg-[#003087] text-white">{user.formation_code}</Badge>
                {user.formation_title && <span className="text-gray-700">{user.formation_title}</span>}
              </div>
            )}
          </CardContent>
        </Card>

        <MesFormations />

        <EmargementApprenant />

        <DocumentsApprenant />

        {/* Resources */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-[#003087] flex items-center gap-2">
                <Download className="h-5 w-5" /> Mes ressources téléchargeables
              </CardTitle>
              <CardDescription>{resources.length} document(s) disponible(s)</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={load}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-gray-500">Chargement…</p>
            ) : resources.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">
                  Aucune ressource disponible pour le moment.
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Contactez la formatrice si vous pensez qu'il s'agit d'une erreur.
                </p>
              </div>
            ) : (
              <div className="grid gap-3">
                {resources.map((r) => (
                  <div
                    key={r.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-gray-200 rounded-lg hover:border-[#F07B1D]/40 hover:shadow-md transition-all bg-white"
                    data-testid={`apprenant-resource-${r.id}`}
                  >
                    <div className="w-11 h-11 rounded-lg bg-[#F07B1D]/10 flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-[#F07B1D]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-semibold text-[#003087]">{r.title}</p>
                        <Badge variant="outline" className="text-[10px]">{categoryLabel(r.category)}</Badge>
                      </div>
                      {r.description && <p className="text-xs text-gray-600 mb-1">{r.description}</p>}
                      <p className="text-[11px] text-gray-500 font-mono truncate">
                        {r.original_filename} · {formatBytes(r.size_bytes)}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleDownload(r)}
                      disabled={downloadingId === r.id}
                      className="bg-[#F07B1D] hover:bg-[#D96A0F] text-white font-bold shrink-0"
                      data-testid={`apprenant-download-btn-${r.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      {downloadingId === r.id ? "..." : "Télécharger"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info footer */}
        <Card className="bg-[#F5F7FA] border-none">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-[#F07B1D] shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-semibold text-[#003087] mb-1">Traçabilité pédagogique</p>
                <p>
                  Chaque connexion et chaque téléchargement effectué depuis cet espace est horodaté
                  et conservé dans le journal de suivi de votre formation (article D6313-3-1 du Code du travail).
                  Ces éléments constituent les <strong>preuves d'assiduité et de réalisation</strong> exigibles par les financeurs et l'auditeur Qualiopi.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <span>© 2026 MGB School — Espace apprenant</span>
        <span className="flex gap-4">
          <Link to="/confidentialite" className="hover:text-[#F07B1D] underline">Confidentialité</Link>
          <button type="button" onClick={reopenCookieBanner} className="hover:text-[#F07B1D] underline" data-testid="apprenant-manage-cookies">Gérer mes cookies</button>
        </span>
      </footer>
    </div>
  );
};
